// Derivations shared by every surface that reads a pool snapshot (the waiting
// stage, the app-wide live-call strip, the membership provider). One source of
// truth for "which answered line lands on this agent next" and for the
// business-facts line an agent reads before saying the company name.

import type { PooledDialerLegView, PooledDialerMemberView } from '../../../shared/types/pooled-dialer';

/** Answered-but-unbridged lines, oldest first — the order they bridge in. */
export function answeredLegsOldestFirst(legs: PooledDialerLegView[]): PooledDialerLegView[] {
  return legs
    .filter((leg) => leg.state === 'screening' || leg.state === 'holding')
    .sort((a, b) => Date.parse(a.stateSince) - Date.parse(b.stateSince));
}

/**
 * The answered line most likely to land on this agent next.
 *
 * Free members read the SERVER's forecast: the engine dry-runs its own
 * filter-then-order assignment (bridge cutoffs, then priority + idle) and
 * stamps upNextLegId on each member view — a positional guess here would be
 * wrong whenever eligibility splits the queue. Best-effort as ever: a line
 * screened out as a machine drops away and the next snapshot re-stamps.
 *
 * A wrap-up member is not in the engine's queue yet (they re-enter behind
 * everyone currently free when they press Next), so a positional guess is
 * all anyone can offer them.
 */
export function upNextLegFor(
  members: PooledDialerMemberView[],
  answeredLegs: PooledDialerLegView[],
  me: PooledDialerMemberView
): PooledDialerLegView | null {
  if (me.state === 'free') {
    if (!me.upNextLegId) return null;
    return answeredLegs.find((leg) => leg.legId === me.upNextLegId) ?? null;
  }
  if (me.state !== 'wrapup') return null;
  const freeCount = members.filter((member) => member.state === 'free').length;
  return answeredLegs[freeCount] ?? null;
}

/**
 * The member an agent's own stage renders.
 *
 * The SSE snapshot is the right source for everything positional — queue
 * place, lines ringing, who is up next — and the stage is deliberately blanked
 * when that snapshot goes stale, so nobody reads a frozen floor as the current
 * one. But an agent live with a prospect loses more than a stale queue
 * position when that happens: the stage is where their prospect's name and
 * number are, and a reconnect notice in its place leaves them talking to
 * someone they cannot see. The membership poll is an independent 3-second read
 * of the same engine, so it carries the stage while the stream is away.
 *
 * Only `busy` falls back. Every other stage is built from snapshot arrays
 * (legs, events, other members); rendering those from a lone member would put
 * confidently wrong numbers on screen, which is what the staleness gates
 * exist to prevent.
 */
export function poolStageMember(
  streamMember: PooledDialerMemberView | null,
  polledMembership: { campaignId: string; member: PooledDialerMemberView } | null,
  campaignId: string,
  streamUsable: boolean
): PooledDialerMemberView | null {
  if (streamUsable && streamMember) return streamMember;
  if (polledMembership?.campaignId !== campaignId) return null;
  return polledMembership.member.state === 'busy' ? polledMembership.member : null;
}
